<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  getContactDisplayName,
  getContactSecondaryDetails,
  getContactCopyValue,
  getContactCompany,
} from 'dashboard/helper/contactIdentifier';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
    monitorMode: name === 'monitor_conversation',
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);
const contactRecord = computed(() => {
  const sender = props.chat.meta.sender || {};
  const storedContact = currentContact.value || {};

  return {
    ...sender,
    ...storedContact,
    source_id:
      storedContact?.source_id ||
      sender?.source_id ||
      props.chat?.contact_inbox?.source_id ||
      props.chat?.messages?.[0]?.conversation?.contact_inbox?.source_id,
    additional_attributes: {
      ...(sender.additional_attributes || {}),
      ...(storedContact.additional_attributes || {}),
    },
    custom_attributes: {
      ...(sender.custom_attributes || {}),
      ...(storedContact.custom_attributes || {}),
    },
    contact_inboxes:
      storedContact.contact_inboxes || sender.contact_inboxes || [],
  };
});
const contactDisplayName = computed(() =>
  getContactDisplayName(contactRecord.value)
);
const contactCompany = computed(() => getContactCompany(contactRecord.value));
const hasContactCompany = computed(
  () =>
    !!contactCompany.value && contactCompany.value !== contactDisplayName.value
);
const showCompanyInline = computed(
  () => hasContactCompany.value && width.value >= 1280
);
const contactSecondaryDetails = computed(() =>
  getContactSecondaryDetails(contactRecord.value)
);
const primaryContactDetail = computed(
  () => contactSecondaryDetails.value[0] || ''
);
const extraContactDetails = computed(() =>
  contactSecondaryDetails.value.slice(1)
);
const contactCopyValue = computed(() =>
  getContactCopyValue(contactRecord.value)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const isWhatsAppCloudConversation = computed(
  () =>
    inbox.value?.channel_type === 'Channel::Whatsapp' &&
    inbox.value?.provider === 'whatsapp_cloud'
);

const isWhatsappContactBlocked = computed(
  () => isWhatsAppCloudConversation.value && !!contactRecord.value?.blocked
);

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !contactRecord.value?.blocked
);

const copyIdentifier = async () => {
  if (!contactCopyValue.value) return;
  await copyTextToClipboard(contactCopyValue.value);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="chatmigo-conversation-header relative z-20 flex w-full min-w-0 flex-1 flex-col items-start justify-between gap-2.5 overflow-visible px-3 py-2.5 xl:flex-row xl:items-start xl:gap-3 xl:min-h-[4rem]"
  >
    <div
      class="chatmigo-header-identity flex w-full min-w-0 max-w-full items-start justify-start xl:w-auto xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <Avatar
        :name="contactDisplayName"
        :src="contactRecord.thumbnail"
        :size="32"
        :status="contactRecord.availability_status"
        hide-offline-status
      />
      <div
        class="chatmigo-header-body ms-2 flex min-w-0 flex-1 flex-col items-start overflow-hidden"
      >
        <div
          class="chatmigo-header-name-row m-0 flex max-w-full items-center gap-1 p-0"
        >
          <span
            class="chatmigo-header-name block min-w-0 truncate text-sm font-medium leading-tight text-n-slate-12"
            :title="contactDisplayName"
          >
            {{ contactDisplayName }}
          </span>
          <span
            v-if="showCompanyInline"
            class="chatmigo-meta-pill chatmigo-meta-pill--company chatmigo-header-company"
            :title="contactCompany"
          >
            {{ contactCompany }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          v-if="
            primaryContactDetail ||
            extraContactDetails.length ||
            (hasContactCompany && !showCompanyInline)
          "
          class="chatmigo-header-secondary mt-0.5 flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-n-slate-10"
        >
          <button
            v-if="primaryContactDetail"
            v-tooltip="$t('CONVERSATION.HEADER.COPY_IDENTIFIER')"
            type="button"
            class="chatmigo-header-copy chatmigo-contact-chip inline-flex min-w-0 max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-left text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
            @click="copyIdentifier"
          >
            <span class="truncate">
              {{ primaryContactDetail }}
            </span>
            <fluent-icon
              v-if="contactCopyValue"
              icon="copy"
              size="12"
              class="flex-shrink-0"
            />
          </button>
          <span
            v-if="hasContactCompany && !showCompanyInline"
            class="chatmigo-meta-pill chatmigo-meta-pill--company chatmigo-meta-pill--company-secondary"
            :title="contactCompany"
          >
            {{ contactCompany }}
          </span>
          <span
            v-for="detail in extraContactDetails"
            :key="detail"
            class="chatmigo-meta-pill chatmigo-meta-pill--subtle"
            :title="detail"
          >
            {{ detail }}
          </span>
        </div>

        <div
          class="chatmigo-header-meta conversation--header--actions flex max-w-full flex-wrap items-center gap-1 overflow-hidden text-xs text-ellipsis text-n-slate-11"
        >
          <span
            v-if="
              hasMultipleInboxes &&
              (primaryContactDetail || extraContactDetails.length)
            "
            aria-hidden="true"
            class="inline-block size-1 rounded-full bg-n-slate-8"
          />
          <InboxName
            v-if="hasMultipleInboxes"
            :inbox="inbox"
            class="chatmigo-meta-pill chatmigo-meta-pill--inbox !mx-0"
          />
          <span
            v-if="isWhatsappContactBlocked"
            class="chatmigo-meta-pill chatmigo-meta-pill--danger"
          >
            <fluent-icon icon="prohibited" size="12" />
            {{ $t('CONVERSATION.SIDEBAR.WHATSAPP_BLOCKED_STATUS') }}
          </span>
          <span
            v-if="isSnoozed"
            aria-hidden="true"
            class="inline-block size-1 rounded-full bg-n-slate-8"
          />
          <span
            v-if="isSnoozed"
            class="chatmigo-meta-pill chatmigo-meta-pill--warning"
          >
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="chatmigo-conversation-header-actions header-actions-wrap flex w-full max-w-full flex-shrink-0 flex-row flex-wrap items-center justify-start gap-2 overflow-visible xl:ml-auto xl:w-auto xl:self-center xl:justify-end"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton
        v-if="!readOnly"
        :inbox="inbox"
        :chat="currentChat"
      />
      <MoreActions v-if="!readOnly" :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
